import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  GetApp as GetAppIcon,
  Phone as PhoneIcon,
  Computer as ComputerIcon,
  Tablet as TabletIcon,
  Share as ShareIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useDeviceDetection, usePWA } from '../contexts/PWAContext';

interface InstallationGuideProps {
  open: boolean;
  onClose: () => void;
  isReinstalling?: boolean;
}

export const InstallationGuide: React.FC<InstallationGuideProps> = ({
  open,
  onClose,
  isReinstalling = false,
}) => {
  const deviceInfo = useDeviceDetection();
  const { pwaState, getAppStoreUrl, getPlayStoreUrl } = usePWA();

  // Calculate installation options
  const installationOptions = (() => {
    const {
      isIOS,
      isAndroid,
      isDesktop,
      canInstallPWA,
      canInstallMobileApp,
      mobileAppCompatible,
      browser,
    } = deviceInfo;

    const isStandalone = pwaState.isStandalone;
    const isPWAInstalled = pwaState.isInstalled;

    // Don't show any installation options if app is already running as PWA or installed
    if (isStandalone || isPWAInstalled) {
      return {
        showPWAInstall: false,
        showMobileAppInstall: false,
        showIOSInstructions: false,
        showAndroidInstructions: false,
        showDesktopInstructions: false,
        mobileAppStore: false,
      };
    }

    return {
      showPWAInstall: canInstallPWA,
      showMobileAppInstall: canInstallMobileApp && mobileAppCompatible,
      showIOSInstructions:
        isIOS &&
        canInstallPWA &&
        (!mobileAppCompatible || browser !== 'Safari'),
      showAndroidInstructions:
        isAndroid && canInstallPWA && !mobileAppCompatible,
      showDesktopInstructions: isDesktop && canInstallPWA,
      mobileAppStore: canInstallMobileApp && mobileAppCompatible,
    };
  })();

  const handleMobileAppInstall = () => {
    if (deviceInfo.isIOS) {
      window.open(getAppStoreUrl(), '_blank');
    } else if (deviceInfo.isAndroid) {
      window.open(getPlayStoreUrl(), '_blank');
    }
    onClose();
  };

  const handlePWAInstall = () => {
    // This will trigger the browser's PWA install prompt
    // The actual installation is handled by the browser
    onClose();
  };

  // Get comprehensive installation instructions based on OS and browser
  const getInstallationInstructions = () => {
    const { isIOS, isAndroid, isDesktop, browser } = deviceInfo;

    if (isIOS) {
      return getIOSInstallationInstructions(browser);
    }

    if (isAndroid) {
      return getAndroidInstallationInstructions(browser);
    }

    if (isDesktop) {
      return getDesktopInstallationInstructions(browser);
    }

    return getGenericInstructions();
  };

  const getIOSInstallationInstructions = (browser: string) => {
    const instructions = {
      title: 'Install on iPhone/iPad',
      subtitle: 'Follow these steps to install the app on your iOS device:',
      steps: [] as string[],
      tips: [] as string[],
    };

    switch (browser) {
      case 'Safari':
        instructions.steps = [
          'Tap the Share button (square with arrow pointing up)',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear on your home screen like a native app',
          'You can organize it in folders and move it around',
          'The app will work offline for cached features',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
          '**Environment-facing camera** selection by default',
        ];
        break;

      case 'Chrome':
        instructions.steps = [
          'Tap the menu button (three dots) in the top right',
          'Select "Add to Home Screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'Chrome PWAs on iOS behave like Safari PWAs',
          'The app will appear on your home screen',
          'You can access it like any other app',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      case 'Firefox':
        instructions.steps = [
          'Tap the menu button (three lines) in the bottom right',
          'Select "Add to Home Screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear on your home screen',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      case 'Edge':
        instructions.steps = [
          'Tap the menu button (three dots) in the top right',
          'Select "Add to Home Screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear on your home screen',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      default:
        instructions.steps = [
          'Look for an "Add to Home Screen" option in your browser menu',
          'Follow the browser-specific prompts',
          'Confirm the installation when prompted',
        ];
        instructions.tips = [
          'The app will work offline for cached features',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
    }

    return instructions;
  };

  const getAndroidInstallationInstructions = (browser: string) => {
    const instructions = {
      title: 'Install on Android',
      subtitle: 'Follow these steps to install the app on your Android device:',
      steps: [] as string[],
      tips: [] as string[],
    };

    switch (browser) {
      case 'Chrome':
        instructions.steps = [
          'Tap the menu button (three dots) in the top right',
          'Select "Add to Home screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear in your app drawer and home screen',
          'You can pin it to your home screen for quick access',
          'The app will work offline for cached features',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
          '**Environment-facing camera** selection by default',
        ];
        break;

      case 'Firefox':
        instructions.steps = [
          'Tap the menu button (three lines) in the top right',
          'Select "Add to Home Screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear on your home screen',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      case 'Samsung Internet':
        instructions.steps = [
          'Tap the menu button (three dots) in the top right',
          'Select "Add page to"',
          'Choose "Home screen"',
          'Tap "Add" to confirm',
        ];
        instructions.tips = [
          'The app will appear on your home screen',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      case 'Edge':
        instructions.steps = [
          'Tap the menu button (three dots) in the top right',
          'Select "Add to Home screen"',
          'Tap "Add" to confirm the installation',
        ];
        instructions.tips = [
          'The app will appear on your home screen',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
        break;

      default:
        instructions.steps = [
          'Look for an "Add to Home Screen" option in your browser menu',
          'Follow the browser-specific prompts',
          'Confirm the installation when prompted',
        ];
        instructions.tips = [
          'The app will work offline for cached features',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing** for better QR code detection',
        ];
    }

    return instructions;
  };

  const getDesktopInstallationInstructions = (browser: string) => {
    const instructions = {
      title: 'Install on Desktop',
      subtitle: 'Follow these steps to install the app on your desktop:',
      steps: [] as string[],
      tips: [] as string[],
    };

    switch (browser) {
      case 'Chrome':
        // Chrome behavior is similar across platforms, but we can detect macOS from user agent
        const isMacOS = navigator.userAgent.includes('Mac OS X');
        if (isMacOS) {
          instructions.steps = [
            'Look for the install icon (plus sign) in the address bar',
            'Click the install icon or "Install" button',
            'Click "Install" in the popup to confirm',
          ];
          instructions.tips = [
            'The app will be installed in your Applications folder',
            'You can launch it like any other macOS app',
            'It will appear in your Dock and Applications folder',
            '**Enhanced camera access** for barcode scanning',
            '**Image processing controls** (brightness, contrast, zoom)',
            '**Smart platform detection** with desktop optimizations',
          ];
        } else {
          instructions.steps = [
            'Look for the install icon (plus sign) in the address bar',
            'Click the install icon or "Install" button',
            'Click "Install" in the popup to confirm',
          ];
          instructions.tips = [
            'The app will be installed and can be launched from your desktop',
            'You can pin it to your taskbar for quick access',
            'It will appear in your Start menu (Windows) or Applications (Linux)',
            '**Enhanced camera access** for barcode scanning',
            '**Image processing controls** (brightness, contrast, zoom)',
            '**Smart platform detection** with desktop optimizations',
          ];
        }
        break;

      case 'Safari':
        instructions.steps = [
          'Click "Share" button in the toolbar',
          'Select "Add to Dock" from the menu',
          'The app will be added to your Dock',
        ];
        instructions.tips = [
          'The app will appear in your Dock like a native app',
          'You can also find it in your Applications folder',
          'It will work offline for cached features',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing controls** (brightness, contrast, zoom)',
        ];
        break;

      case 'Firefox':
        instructions.steps = [
          'Click the menu button (three lines) in the top right',
          'Select "Install App"',
          'Click "Install" in the popup to confirm',
        ];
        instructions.tips = [
          'The app will be installed and can be launched from your desktop',
          'You can pin it to your taskbar for quick access',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing controls** (brightness, contrast, zoom)',
        ];
        break;

      case 'Edge':
        instructions.steps = [
          'Look for the install icon (plus sign) in the address bar',
          'Click the install icon or "Install" button',
          'Click "Install" in the popup to confirm',
        ];
        instructions.tips = [
          'The app will be installed and can be launched from your desktop',
          'You can pin it to your taskbar for quick access',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing controls** (brightness, contrast, zoom)',
        ];
        break;

      default:
        instructions.steps = [
          'Look for an install option in your browser menu',
          'Follow the browser-specific prompts',
          'Confirm the installation when prompted',
        ];
        instructions.tips = [
          'The exact steps may vary depending on your browser and device',
          'The app will work offline for cached features',
          'You can access it like a native app once installed',
          '**Enhanced camera access** for barcode scanning',
          '**Image processing controls** (brightness, contrast, zoom)',
        ];
    }

    return instructions;
  };

  const getGenericInstructions = () => {
    return {
      title: 'Install App',
      subtitle: 'Follow these general steps to install the app:',
      steps: [
        'Look for an install option in your browser menu',
        'Follow the browser-specific prompts',
        'Confirm the installation when prompted',
      ],
      tips: [
        'The exact steps may vary depending on your browser and device',
        'The app will work offline for cached features',
        'You can access it like a native app once installed',
        '**Enhanced camera access** for barcode scanning',
        '**Image processing controls** (brightness, contrast, zoom)',
      ],
    };
  };

  const renderDeviceInfo = () => {
    if (!deviceInfo) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Device: {deviceInfo.deviceType.toUpperCase()} • OS:{' '}
          {deviceInfo.osVersion || 'Unknown'} • Browser: {deviceInfo.browser}
        </Typography>
        {!deviceInfo.mobileAppCompatible && deviceInfo.isMobile && (
          <Chip
            label="Older device detected"
            color="warning"
            size="small"
            sx={{ mt: 1 }}
          />
        )}
      </Box>
    );
  };

  const renderInstallationOptions = () => {
    if (!installationOptions) return null;

    return (
      <Grid container spacing={2}>
        {installationOptions.showMobileAppInstall && (
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Alternative: Mobile App</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Install our native mobile app as an alternative option
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  startIcon={<GetAppIcon />}
                  onClick={handleMobileAppInstall}
                  fullWidth
                >
                  Install App
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {installationOptions.showPWAInstall && (
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ComputerIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Recommended: Web App</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Install as a web app with enhanced multi-platform scanning
                  capabilities, smart platform detection, and automatic updates
                  - our recommended option
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={handlePWAInstall}
                  fullWidth
                >
                  Install Web App
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderInstructions = () => {
    if (!installationOptions) return null;

    const instructions = getInstallationInstructions();

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {instructions.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {instructions.subtitle}
        </Typography>
        <List dense>
          {instructions.steps.map((step, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Typography variant="h6" color="primary">
                  {index + 1}
                </Typography>
              </ListItemIcon>
              <ListItemText primary={step} />
            </ListItem>
          ))}
        </List>
        {instructions.tips.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <InfoIcon
                sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }}
              />
              <strong>Tips:</strong>
            </Typography>
            <List dense sx={{ mt: 1 }}>
              {instructions.tips.map((tip, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={tip}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">
            {isReinstalling ? 'Reinstall Embue Super' : 'Install Embue Super'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {renderDeviceInfo()}
        {renderInstallationOptions()}
        {renderInstructions() && (
          <>
            <Divider sx={{ my: 2 }} />
            {renderInstructions()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
